import { IconSet } from '@postscript/components';
import {
  CreateOfferRequest,
  DiscountValueQualifier,
  ManualOfferKind,
  Offer,
  OfferFormValues,
  OfferSource,
  OfferStatus,
  OfferType,
  UpdateOfferRequest,
} from 'components/brand/offers/types';
import { FieldMetaProps } from 'formik';
import { DateTime } from 'luxon';
import * as yup from 'yup';

export const shouldShowFormFieldError = (
  meta: FieldMetaProps<any>,
  isDirty: boolean,
  submitCount: number,
): boolean => {
  // Util function to prevent repetition and bugs. This fixes a regression where
  // some fields didn't have this condition properly updated to show the error.
  return (
    (meta.touched && isDirty && !!meta.error) ||
    (submitCount > 0 && !!meta.error)
  );
};

export const getStatusBadgeVariant = (status: OfferStatus) => {
  switch (status) {
    case OfferStatus.ACTIVE:
    case OfferStatus.PRE_PROMOTION:
      return 'success';
    case OfferStatus.SCHEDULED:
      return 'info';
    case OfferStatus.ENDED:
      return 'neutral';
    case OfferStatus.DRAFT:
      return 'warning';
    default:
      return 'neutral';
  }
};

export const formatOfferStatusText = (status: OfferStatus): string => {
  switch (status) {
    case OfferStatus.PRE_PROMOTION:
      return 'Pre-Promotion';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }
};

export const formatOfferSourceText = (source: OfferSource): string => {
  if (source === OfferSource.CASHBACK) {
    return 'Cashback';
  }

  if (source === OfferSource.SHOPIFY || source === OfferSource.SHOPIFY_SYNC) {
    return 'Shopify';
  }

  return 'Offer';
};

export const formatOfferTypeText = (
  offerType: OfferType,
  offerSource: OfferSource,
): string => {
  if (offerSource === OfferSource.CASHBACK) {
    return offerType === OfferType.AUTOMATIC_DISCOUNT
      ? 'Sitewide Promotion'
      : 'URL Based Promotion';
  }

  return offerType === OfferType.AUTOMATIC_DISCOUNT
    ? 'Automatic Discount'
    : offerType === OfferType.UNIQUE_DISCOUNT_CODE
    ? 'Unique Discount Code'
    : 'Static Discount Code';
};

export const getOfferTypeIcon = (
  offerType: OfferType,
  offerSource: OfferSource,
): (() => JSX.Element) => {
  if (offerSource === OfferSource.CASHBACK) {
    return offerType === OfferType.AUTOMATIC_DISCOUNT
      ? IconSet.Globe
      : IconSet.Link;
  }

  if (offerType === OfferType.AUTOMATIC_DISCOUNT) {
    return IconSet.Tag;
  }
  if (offerType === OfferType.UNIQUE_DISCOUNT_CODE) {
    return IconSet.User;
  }
  return IconSet.Barcode;
};

export const formatPromotionTypeText = (isSitewideSale: boolean): string => {
  return isSitewideSale ? 'Onsite Promotion' : 'Strategic Offer';
};

export const formatOfferDate = (dateString: string | null): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  } catch {
    return '-';
  }
};

export const getUserTimezoneAbbreviation = (): string => {
  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = DateTime.now().setZone(userTimezone);

    if (!now.isValid) {
      return '';
    }

    // Get the timezone abbreviation (e.g., "PDT", "EST", "PST")
    return now.toFormat('ZZZZ');
  } catch {
    return '';
  }
};

export const parseFormDateTime = (
  dateString: string,
  timeString: string,
): DateTime => {
  return DateTime.fromFormat(`${dateString} ${timeString}`, 'yyyy-MM-dd H:mm');
};

export const convertOfferFormDateTimeToISODateTime = (
  dateString: string,
  timeString: string,
  timezone?: string,
): string => {
  const userTimezone =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localDateTimeString = `${dateString}T${timeString}:00`;
  const utcDateTime = DateTime.fromISO(localDateTimeString, {
    zone: userTimezone,
  })
    .toUTC()
    .toISO();

  return utcDateTime || '';
};

export const convertISODateTimeToFormDateTime = (
  isoString: string | null,
): { date: string; time: string } => {
  if (!isoString) {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    return { date: today, time: '09:00' };
  }

  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localDateTime = DateTime.fromISO(isoString).setZone(userTimezone);

    if (!localDateTime.isValid) {
      const today = DateTime.now().toFormat('yyyy-MM-dd');
      return { date: today, time: '09:00' };
    }

    const date = localDateTime.toFormat('yyyy-MM-dd');
    const time = localDateTime.toFormat('HH:mm');

    return { date, time };
  } catch {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    return { date: today, time: '09:00' };
  }
};

// % off / $ off capture a structured float amount that becomes the persisted
// discount value text.
export const isStructuredDiscountKind = (
  manualOfferKind?: ManualOfferKind,
): boolean =>
  manualOfferKind === ManualOfferKind.PERCENT_OFF ||
  manualOfferKind === ManualOfferKind.AMOUNT_OFF;

// Composes the structured % off / $ off control into a human-readable discount
// value string (e.g. "Up to 20% off", "Exactly $15 off"). Returns '' when no
// amount has been entered.
export const formatStructuredDiscountValue = (
  qualifier: DiscountValueQualifier | undefined,
  amount: string | number | undefined,
  valueType: 'percent' | 'amount',
): string => {
  const trimmedAmount = amount == null ? '' : String(amount).trim();
  if (!trimmedAmount) {
    return '';
  }

  const qualifierLabel =
    qualifier === DiscountValueQualifier.UP_TO ? 'Up to' : 'Exactly';
  const amountLabel =
    valueType === 'percent' ? `${trimmedAmount}% off` : `$${trimmedAmount} off`;

  return `${qualifierLabel} ${amountLabel}`;
};

// Inverse of formatStructuredDiscountValue: recovers the structured % off /
// $ off fields from a persisted discount value string (e.g. "Up to 20% off",
// "Exactly $15 off"). Returns null for anything else (freeform values), so
// callers fall back to the freeform discount value field.
const STRUCTURED_DISCOUNT_VALUE_PATTERN =
  /^(Exactly|Up to) (?:\$(\d*\.?\d+)|(\d*\.?\d+)%) off$/;

export const parseStructuredDiscountValue = (
  discountValue: string | null | undefined,
): Pick<
  OfferFormValues,
  'manualOfferKind' | 'discountValueQualifier' | 'discountAmount'
> | null => {
  if (!discountValue) {
    return null;
  }

  const match = discountValue.trim().match(STRUCTURED_DISCOUNT_VALUE_PATTERN);
  if (!match) {
    return null;
  }

  const [, qualifierLabel, dollarAmount, percentAmount] = match;

  return {
    manualOfferKind:
      dollarAmount != null
        ? ManualOfferKind.AMOUNT_OFF
        : ManualOfferKind.PERCENT_OFF,
    discountValueQualifier:
      qualifierLabel === 'Up to'
        ? DiscountValueQualifier.UP_TO
        : DiscountValueQualifier.EXACTLY,
    discountAmount: dollarAmount ?? percentAmount,
  };
};

export const convertOfferFormValuesToRequest = ({
  values,
  existingOffer,
  isDraft = false,
  isSitewideSale,
}: {
  values: OfferFormValues;
  existingOffer?: Offer | null;
  isDraft?: boolean;
  isSitewideSale?: boolean;
}): CreateOfferRequest | UpdateOfferRequest => {
  const startsAt =
    values.startDate && values.startTime
      ? convertOfferFormDateTimeToISODateTime(
          values.startDate,
          values.startTime,
        )
      : null;

  const endsAt =
    values.setEndDate && values.endDate && values.endTime
      ? convertOfferFormDateTimeToISODateTime(values.endDate, values.endTime)
      : null;

  const promoteFrom =
    values.prePromotionControl &&
    values.promoteStartDate &&
    values.promoteStartTime
      ? convertOfferFormDateTimeToISODateTime(
          values.promoteStartDate,
          values.promoteStartTime,
        )
      : null;

  const discountCode = values.discountCode?.trim() || null;
  const discountValue = isStructuredDiscountKind(values.manualOfferKind)
    ? formatStructuredDiscountValue(
        values.discountValueQualifier,
        values.discountAmount,
        values.manualOfferKind === ManualOfferKind.PERCENT_OFF
          ? 'percent'
          : 'amount',
      ) || null
    : values.discountValue?.trim() || null;

  const baseRequest = {
    title: values.title,
    description: values.description,
    offerType: values.offerType as string,
    source: values.offerSource,
    discountCode,
    discountValue,
    isDraft,
    isStackableWithEvergreen: values.allowStacking,
    startsAt,
    endsAt,
    promoteFrom,
    isSitewideSale,
    discountRuleId: values.discountRuleId,
    segmentId: values.segmentId ?? null,
    manualOfferKind: values.manualOfferKind,
    discountAmount:
      values.discountAmount === '' || values.discountAmount == null
        ? undefined
        : values.discountAmount,
  };

  if (existingOffer) {
    return baseRequest as UpdateOfferRequest;
  }
  return baseRequest as CreateOfferRequest;
};

export const convertOfferToFormValues = (offer: Offer): OfferFormValues => {
  const startDateTime = convertISODateTimeToFormDateTime(offer.startsAt);
  const endDateTime = convertISODateTimeToFormDateTime(offer.endsAt);
  const promoteDateTime = convertISODateTimeToFormDateTime(offer.promoteFrom);
  // Structured %/$ off fields aren't persisted, only their flattened discount
  // value string is, so recover them to re-render the structured control.
  const structuredDiscount = parseStructuredDiscountValue(offer.discountValue);

  return {
    ...(structuredDiscount ?? {}),
    offerSource: offer.source,
    offerType: offer.offerType,
    discountCode: offer.discountCode || '',
    discountValue: offer.discountValue || '',
    startDate: startDateTime.date,
    startTime: startDateTime.time,
    setEndDate: !!offer.endsAt,
    endDate: endDateTime.date,
    endTime: endDateTime.time,
    title: offer.title,
    description: offer.description,
    prePromotionControl: !!offer.promoteFrom,
    promoteStartDate: promoteDateTime.date,
    promoteStartTime: promoteDateTime.time,
    allowStacking: offer.isStackableWithEvergreen,
    discountRuleId: offer.discountRuleId,
    segmentId: offer.segmentId ?? null,
  };
};

export const getSaveButtonText = (values: OfferFormValues): string => {
  // If end date is set and is in the past, just show "Save"
  if (values.setEndDate && values.endDate && values.endTime) {
    const endDateTime = parseFormDateTime(values.endDate, values.endTime);
    const now = DateTime.now();

    if (endDateTime.isValid && endDateTime < now) {
      return 'Save';
    }
  }

  // If start date is in the future, show "Schedule"
  // If start date is in the past or present, show "Activate"
  if (values.startDate && values.startTime) {
    const startDateTime = parseFormDateTime(values.startDate, values.startTime);
    const now = DateTime.now();

    if (startDateTime.isValid) {
      return startDateTime > now ? 'Schedule' : 'Activate';
    }
  }

  return 'Save';
};

// Character limits for form fields
export const OFFER_FORM_MAX_TITLE_CHARS = 500;
export const OFFER_FORM_MAX_DESCRIPTION_CHARS = 10000;
export const OFFER_FORM_MAX_DISCOUNT_VALUE_CHARS = 500;
export const OFFER_FORM_MAX_DISCOUNT_CODE_CHARS = 5000;

export const validationSchema = (
  offerType?: OfferType,
  manualOfferKind?: ManualOfferKind,
) => {
  const isUniqueDiscountCode = offerType === OfferType.UNIQUE_DISCOUNT_CODE;
  const requiresDiscountAmount = isStructuredDiscountKind(manualOfferKind);
  // Non-unique offers require the freeform discount_value, except structured
  // %/$ kinds where the structured amount field is the discount value instead.
  const requiresDiscountValue =
    !isUniqueDiscountCode && !requiresDiscountAmount;

  return yup.object().shape({
    discountAmount: requiresDiscountAmount
      ? yup
          .number()
          .transform((value, originalValue) =>
            String(originalValue).trim() === '' ? undefined : value,
          )
          .typeError('Discount value must be a number')
          .required('Discount value is required')
          .moreThan(0, 'Discount value must be greater than 0')
      : yup.mixed().notRequired(),
    offerType: yup
      .string()
      .oneOf(Object.values(OfferType))
      .required('Offer type is required'),
    discountCode: yup.string().when(['offerType', 'offerSource'], {
      is: (offerType: OfferType) => offerType === OfferType.DISCOUNT_CODE,
      then: (schema) => {
        const baseSchema = schema
          .required('Discount code is required')
          .max(
            OFFER_FORM_MAX_DISCOUNT_CODE_CHARS,
            `Discount code must be ${OFFER_FORM_MAX_DISCOUNT_CODE_CHARS} characters or less`,
          );

        // Add URL validation for cashback offers
        return baseSchema.when('offerSource', {
          is: (offerSource: OfferSource) =>
            offerSource === OfferSource.CASHBACK,
          then: (urlSchema) =>
            urlSchema.url('Promotion URL Code must be a valid URL'),
        });
      },
    }),
    discountValue: requiresDiscountValue
      ? yup
          .string()
          .required('Discount value is required')
          .test('non-empty', 'Discount value cannot be empty', (value) => {
            return (
              value !== null && value !== undefined && value.trim().length > 0
            );
          })
          .max(
            OFFER_FORM_MAX_DISCOUNT_VALUE_CHARS,
            `Discount value must be ${OFFER_FORM_MAX_DISCOUNT_VALUE_CHARS} characters or less`,
          )
      : yup
          .string()
          .max(
            OFFER_FORM_MAX_DISCOUNT_VALUE_CHARS,
            `Discount value must be ${OFFER_FORM_MAX_DISCOUNT_VALUE_CHARS} characters or less`,
          ),
    startDate: yup.string().required('Start date is required'),
    startTime: yup.string().required('Start time is required'),
    setEndDate: yup.boolean(),
    title: yup
      .string()
      .trim()
      .required('Title is required')
      .min(1, 'Title must be at least 1 character')
      .max(
        OFFER_FORM_MAX_TITLE_CHARS,
        `Title must be ${OFFER_FORM_MAX_TITLE_CHARS} characters or less`,
      ),
    description: yup
      .string()
      .trim()
      .required('Description is required')
      .min(1, 'Description must be at least 1 character')
      .max(
        OFFER_FORM_MAX_DESCRIPTION_CHARS,
        `Description must be ${OFFER_FORM_MAX_DESCRIPTION_CHARS} characters or less`,
      ),
    prePromotionControl: yup
      .boolean()
      .required('Pre-promotion control is required'),
    promoteStartDate: yup.string().when('prePromotionControl', {
      is: true,
      then: (schema) =>
        schema
          .required('Promote start date is required')
          .test(
            'promote-before-start',
            'Promote start date and time must be at least 1 minute before offer start date and time',
            function (promoteStartDate) {
              const { promoteStartTime, startDate, startTime } = this.parent;

              if (
                !promoteStartDate ||
                !promoteStartTime ||
                !startDate ||
                !startTime
              ) {
                return true; // Let required validation handle missing fields
              }

              try {
                const promoteStartDateTime = parseFormDateTime(
                  promoteStartDate,
                  promoteStartTime,
                );
                const offerStartDateTime = parseFormDateTime(
                  startDate,
                  startTime,
                );

                if (
                  !promoteStartDateTime.isValid ||
                  !offerStartDateTime.isValid
                ) {
                  return true; // Let backend validation handle it if frontend fails
                }

                const minimumGapMinutes = 1;
                const maxPromoteTime = offerStartDateTime.minus({
                  minutes: minimumGapMinutes,
                });

                return promoteStartDateTime <= maxPromoteTime;
              } catch {
                return true; // If parsing fails, let backend validation handle it
              }
            },
          ),
      otherwise: (schema) => schema,
    }),
    promoteStartTime: yup.string().when('prePromotionControl', {
      is: true,
      then: (schema) => schema.required('Promote start time is required'),
      otherwise: (schema) => schema,
    }),
    endDate: yup.string().when('setEndDate', {
      is: true,
      then: (schema) =>
        schema
          .required('End date is required')
          .test(
            'end-after-start',
            'End date and time must be after start date and time',
            function (endDate) {
              const { startDate, startTime, endTime } = this.parent;

              if (!endDate || !startDate || !startTime || !endTime) {
                return true;
              }

              try {
                const startDateTime = parseFormDateTime(startDate, startTime);
                const endDateTime = parseFormDateTime(endDate, endTime);

                if (!startDateTime.isValid || !endDateTime.isValid) {
                  return true; // Let backend validation handle it if frontend fails
                }

                return endDateTime > startDateTime;
              } catch {
                return true; // Let backend validation handle it if frontend fails
              }
            },
          ),
      otherwise: (schema) => schema,
    }),
    endTime: yup.string().when('setEndDate', {
      is: true,
      then: (schema) => schema.required('End time is required'),
      otherwise: (schema) => schema,
    }),
    allowStacking: yup.boolean(),
  });
};

export const getInitialValues = (
  offerSource: OfferSource,
  offerType: OfferType,
  manualOfferKind?: ManualOfferKind,
): OfferFormValues => {
  const today = DateTime.now().toFormat('yyyy-MM-dd');

  return {
    offerSource,
    offerType,
    discountCode: '',
    discountValue: '',
    startDate: today,
    startTime: '09:00',
    setEndDate: false,
    endDate: today,
    endTime: '17:00',
    title: '',
    description: '',
    prePromotionControl: false, // false = only when live, true = promote in advance
    promoteStartDate: today,
    promoteStartTime: '09:00',
    allowStacking: false,
    discountRuleId: null,
    segmentId: null,
    manualOfferKind,
    discountValueQualifier: DiscountValueQualifier.EXACTLY,
    discountAmount: '',
  };
};
